import React from 'react';
import styled from 'styled-components';
import { intervalToDuration } from 'date-fns';
import { useInterval } from 'react-use';

import { gray } from '@avangard/ui/colors';

type TestingTimerProps = {
    until: number;
    onTimeUp: () => void | Promise<void>;
};

const Root = styled.div`
    color: ${gray[80]};
    font-size: 2rem;
    font-weight: 700;
`;

// variable for save last duration (for saving between rerender)
let lastUntil: number | undefined = undefined;

export const handleSetLastUntil = (time?: number | undefined) => {
    lastUntil = time;
};

const TestingTimer = (props: TestingTimerProps): React.ReactElement => {
    const { until, onTimeUp } = props;

    const [minutes, setMinutes] = React.useState<number>(0);
    const [seconds, setSeconds] = React.useState<number>(0);
    const [isRunning, setRunning] = React.useState<boolean>(true);

    const getIntervalDuration = React.useCallback((end: number): Duration => {
        const result = intervalToDuration({ start: 0, end: end * 1000 });

        return result;
    }, []);

    useInterval(
        () => {
            if (seconds > 0) {
                setSeconds(seconds - 1);
            }

            if (seconds === 0) {
                if (minutes === 0) {
                    setMinutes(0);
                    setSeconds(0);
                    setRunning(false);
                    onTimeUp();
                } else {
                    setMinutes(minutes - 1);
                    setSeconds(59);
                }
            }

            // Saving last until duration for rerender (when question has been changed)
            handleSetLastUntil(minutes * 60 + seconds - 1);
        },
        isRunning ? 1000 : null,
    );

    React.useEffect(() => {
        if (until) {
            const duration = !!lastUntil && until < lastUntil ? until : lastUntil || until;
            const remainder = getIntervalDuration(duration);

            setMinutes(remainder.minutes ?? 0);
            setSeconds(remainder.seconds ?? 0);
        }
    }, [until]);

    const currentTime = `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;

    return <Root>{currentTime}</Root>;
};

export { TestingTimer };
